/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../locales/en-US/descriptors';
import { isObjectOrArrayAndEmpty } from '../../shared/Utils/Utils';
import ConnectionError from '../../shared/ErrorComponents/ConnectionError';
import withLoadingStatus from '../../shared/StatusWrappers/LoadingStatus';
import withErrorStatus from '../../shared/StatusWrappers/ErrorStatus';
import {
  ECartAt,
  IConvertDataForUtilityTrendChart,
  IPerformanceDetailComponentProps,
  ISiteCard,
  IUtilityBillElect,
  IUtilityBillElectData,
  IUtilityPercentProps
} from '../../DashboardKPIWidgets/IEnergyManagementProps';
import UtilityBillPerformanceDetailComponent from './UtilityBillPerformanceDetailComponent';
import { Button, Card, Icon, Tooltip } from '@scuf/common';
import { UtilityType } from '../../shared/Utils/Constants';
import { ApiContext } from '../../../context/ApiContext';

interface IUtilityBillPerfDetail extends ISiteCard {
  data: IUtilityBillElectData;
  UtilityBillType: string;
  isPrevPeriodForAllMonths?: boolean;
  isPrevPeriodForAllMonthsConsumption?: boolean;
}

const UtilityBillPerformanceDetailContent = (props: IUtilityBillPerfDetail) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const {
    data,
    onRefresh,
    UtilityBillType,
    isPrevPeriodForAllMonths,
    isPrevPeriodForAllMonthsConsumption
  } = props;
  if (isObjectOrArrayAndEmpty(data)) {
    return (
      <div className="errorDisplay">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
          onRefresh={onRefresh}
        />
      </div>
    );
  }
  return (
    <div className="electricity-performance-detail-container">
      <div>
        <UtilityBillPerformanceDetailComponent
          data={data}
          UtilityBillType={
            UtilityBillType
          } /* once utilityType fixed from backend side then will remove this*/
          isPrevPeriodForAllMonths={isPrevPeriodForAllMonths}
          isPrevPeriodForAllMonthsConsumption={
            isPrevPeriodForAllMonthsConsumption
          }
        />
      </div>
    </div>
  );
};

const UtilityBillPerformanceDetailWithLoader = withLoadingStatus(
  withErrorStatus(UtilityBillPerformanceDetailContent)
);

const UtilityBillPerformanceDetailWidget = (
  props: IPerformanceDetailComponentProps
) => {
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const {
    dataConnector,
    commonProps,
    UtilityBillType,
    onViewDetailsClick,
    isOverview
  } = props;

  const onRefresh = () =>
    dataConnector && dataConnector.actions && dataConnector.actions.getAll();
  const { all, loading, error, status } = dataConnector.data;
  const UtilityBillSavings = (all as unknown) as IUtilityBillElectData;
  const isDataAvailable = UtilityBillSavings ? true : false;

  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [statusCode, setStatusCode] = useState<number | null>(0);
  const [errorInfo, setErrorInfo] = useState(error);
  const [data, setData] = useState({} as IUtilityBillElectData);
  const [isPrevPeriodForAllMonths, setIsPrevPeriodForAllMonths] = useState<
    boolean
  >(true);
  const [
    isPrevPeriodForAllMonthsConsumption,
    setIsPrevPeriodForAllMonthsConsumption
  ] = useState<boolean>(true);

  const apiContext = useContext(ApiContext);

  const checkIfAllMonthsHasPreviousPeriod = (data: IUtilityBillElectData) => {
    return (
      data &&
      data.monthwiseAggregations &&
      data.monthwiseAggregations.every(
        (item: IConvertDataForUtilityTrendChart) =>
          item.previousPeriodCost !== 0 &&
          item.previousPeriodCost !== null &&
          item.totalActualCost !== 0 &&
          item.totalActualCost
      )
    );
  };

  const checkIfAllSitesHasPreviousPeriod = (data: IUtilityBillElectData) => {
    return (
      data &&
      data.breakdownBySite &&
      data.breakdownBySite.every(
        (item: IUtilityPercentProps) =>
          item.aggregations.previousPeriodCost !== 0 &&
          item.aggregations.previousPeriodCost !== null &&
          item.aggregations.totalActualCost !== 0 &&
          item.aggregations.totalActualCost !== null
      )
    );
  };

  const checkIfAllMonthsHasPreviousPeriodForConsumption = (
    data: IUtilityBillElectData
  ) => {
    return (
      data &&
      data.monthwiseAggregations &&
      data.monthwiseAggregations.every(
        (item: IConvertDataForUtilityTrendChart) =>
          item.previousPeriodConsumption !== 0 &&
          item.previousPeriodConsumption !== null &&
          item.totalActualConsumption !== 0 &&
          item.totalActualConsumption !== null
      )
    );
  };

  const checkIfAllSitesHasPreviousPeriodForConsumption = (
    data: IUtilityBillElectData
  ) => {
    return (
      data &&
      data.breakdownBySite &&
      data.breakdownBySite.every(
        (item: IUtilityPercentProps) =>
          item.aggregations.previousPeriodConsumption !== 0 &&
          item.aggregations.previousPeriodConsumption !== null &&
          item.aggregations.totalActualConsumption !== 0 &&
          item.aggregations.totalActualConsumption !== null
      )
    );
  };

  const checkIfAllSiteHasCompleteData = (data: IUtilityBillElectData) => {
    return (
      data &&
      data.breakdownBySite &&
      data.breakdownBySite.every(
        (item: { isCompleted: boolean }) => item.isCompleted
      )
    );
  };

  const checkIfAllMonthHasCompleteData = (data: IUtilityBillElectData) => {
    return (
      data &&
      data.monthwiseAggregations &&
      data.monthwiseAggregations.every(
        (item: { isCompleted: boolean }) => item.isCompleted
      )
    );
  };

  useEffect(() => {
    setIsLoading(loading);
    if ((isDataAvailable || error || status) && !loading) {
      if (isDataAvailable) {
        if (apiContext.apiCallAt === ECartAt.portfolioLabel) {
          setIsPrevPeriodForAllMonths(
            checkIfAllMonthsHasPreviousPeriod(UtilityBillSavings) &&
              checkIfAllSitesHasPreviousPeriod(UtilityBillSavings) &&
              checkIfAllSiteHasCompleteData(UtilityBillSavings) &&
              checkIfAllMonthHasCompleteData(UtilityBillSavings)
          );
          setIsPrevPeriodForAllMonthsConsumption(
            checkIfAllMonthsHasPreviousPeriodForConsumption(
              UtilityBillSavings
            ) &&
              checkIfAllSitesHasPreviousPeriodForConsumption(
                UtilityBillSavings
              ) &&
              checkIfAllSiteHasCompleteData(UtilityBillSavings) &&
              checkIfAllMonthHasCompleteData(UtilityBillSavings)
          );
        }

        setData(UtilityBillSavings);
      }
      setErrorInfo(error);
      setStatusCode(status);
    }
  }, [isDataAvailable, loading, status]);

  const isDetailedViewEnabled = [
    UtilityType.Electricity,
    UtilityType.Water
  ].includes(UtilityBillType || ''); // remove once implemented for other utilities as well

  return (
    <>
      {/*{isDetailedViewEnabled && isOverview && (
            <div style={{ textAlign: 'right' }}>
              <Button.Group>
              <Button 
              type="secondary" 
              content={t(d.BASIC)} 
              />
              <Button
                type="secondary"
               // content={t(d.VIEWDETAILS)}
                content={t(d.ADVANCED)}         
                onClick={onViewDetailsClick}
              />
              </Button.Group>
            </div>
          )}*/}
      <Card className="emaas-performance-detail-card">
        <Card.Header>
          <div className="emaas-summary-card-header">
            <span>{commonProps.performanceHeading}</span>
            <Tooltip
              element={
                <Icon
                  root="common"
                  name="badge-info"
                  exactSize="0.875rem"
                  className="emaas-summary-card-header-info-icon"
                />
              }
              event="hover"
              content={t(d.UTILITYVIEWDETAILSTOOLTIP)}
              position="right center"
              size="small"
              header={commonProps.performanceHeading}
            />
          </div>
        </Card.Header>

        <Card.Content className="detail-summary-card">
          <UtilityBillPerformanceDetailWithLoader
            loading={isLoading}
            error={errorInfo}
            statusCode={statusCode}
            onRefresh={onRefresh}
            data={data}
            loadingText={t(d.LOADING)}
            title={t(d.CONNECTIONERRORTITLE)}
            noDataMessage={t(d.NODATAAVAILABLE)}
            connectionErrorMessage={t(d.CONNECTIONERROR)}
            UtilityBillType={UtilityBillType!}
            isPrevPeriodForAllMonths={isPrevPeriodForAllMonths}
            isPrevPeriodForAllMonthsConsumption={
              isPrevPeriodForAllMonthsConsumption
            }
          />
          {/* {isDetailedViewEnabled && isOverview && (
            <div style={{ textAlign: 'right' }}>
              <Button
                type="secondary"
               // content={t(d.VIEWDETAILS)}
                content={t(d.ADVANCED)}         
                onClick={onViewDetailsClick}
              />
            </div>
          )} */}
        </Card.Content>
      </Card>
    </>
  );
};
export default UtilityBillPerformanceDetailWidget;
